import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };
type State = { error: Error | null };

/**
 * App-root error boundary. Catches render errors below the root, shows a
 * reload CTA, and prints the underlying message in collapsible detail so
 * the single user can copy/paste it during debugging.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Single-user app: log to console so the dev tools have the stack.
    console.error("[ErrorBoundary]", error, info);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <main className="mx-auto box-border flex w-full max-w-[640px] flex-col gap-4 px-4 py-8">
        <div className="panel space-y-3 border-red-200 bg-red-50 p-5">
          <h1 className="text-base font-semibold text-red-900">Something went wrong</h1>
          <p className="text-sm text-red-800">
            The app hit an unexpected error and stopped rendering. Reload to try again.
          </p>
          <div className="flex gap-2">
            <button className="btn" type="button" onClick={() => window.location.reload()}>
              Reload
            </button>
          </div>
          <details className="text-xs text-red-700">
            <summary className="cursor-pointer select-none">Technical details</summary>
            <pre className="mt-2 whitespace-pre-wrap break-words text-[11px]">{this.state.error.message}</pre>
          </details>
        </div>
      </main>
    );
  }
}
